import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform, useSpring } from 'framer-motion';
import { ArrowRight, Check, User, Users, Star, MousePointer } from 'lucide-react';
import './Hero.css';

// Import mobile version
import HeroMobile from './mobile/Hero_mobile';

// Organic blob backgrounds
const OrganicBlobs = () => (
  <div className="organic-blobs">
    <div className="organic-blob blob-1"></div>
    <div className="organic-blob blob-2"></div>
    <div className="organic-blob blob-3"></div>
    <div className="organic-blob blob-4"></div>
  </div>
);

// Enhanced floating particles with varied colors and sizes
const EnhancedParticles = () => (
  <div className="enhanced-particles">
    {[...Array(30)].map((_, i) => (
      <div 
        key={i} 
        className={`enhanced-particle particle-${i % 6}`}
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          width: `${Math.random() * 8 + 2}px`,
          height: `${Math.random() * 8 + 2}px`,
          animationDelay: `${Math.random() * 10}s`,
          animationDuration: `${Math.random() * 15 + 10}s`
        }}
      />
    ))}
  </div>
);

// Improved headline slider component with better text handling
const HeadlineSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const headlines = [
    "Group Together and Save!",
    "Save $450 on Premium Phone Plans"
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % headlines.length);
    }, 5000); // Switch every 5 seconds

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="headline-slider">
      <AnimatePresence mode="wait">
        {headlines.map((headline, index) => (
          index === currentIndex && (
            <motion.h1 
              key={headline}
              className="hero-title"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
            >
              {headline}
            </motion.h1>
          )
        ))}
      </AnimatePresence>
    </div>
  );
};

// Enhanced feature item with hover effects and advanced styling
const EnhancedFeatureItem = ({ mainText, supportingText, delay = 0, icon }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div 
      className="enhanced-feature"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: 0.6 + delay * 0.15 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      whileHover={{ scale: 1.03 }}
    >
      <motion.div 
        className="enhanced-feature-icon-container"
        animate={{ 
          boxShadow: isHovered 
            ? "0 15px 30px rgba(0, 209, 224, 0.3), 0 0 0 2px rgba(0, 209, 224, 0.4)" 
            : "0 10px 20px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 209, 224, 0.2)"
        }}
      >
        <Check size={18} className="enhanced-feature-icon" />
        <motion.div 
          className="icon-glow"
          animate={{
            opacity: isHovered ? 1 : 0.6,
            scale: isHovered ? 1.2 : 1
          }}
          transition={{ duration: 0.5 }}
        />
      </motion.div>
      <div className="enhanced-feature-text-container">
        <span className="enhanced-feature-text">{mainText}</span>
        <span className="enhanced-feature-subtext">{supportingText}</span>
      </div>
    </motion.div>
  );
};

// Cursor-interactive card component
const InteractiveSavingsCard = () => {
  const cardRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  
  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    const rotateX = (y - centerY) / 20;
    const rotateY = (centerX - x) / 20;
    
    setMousePosition({ rotateX, rotateY });
  };
  
  const springConfig = { damping: 18, stiffness: 300 };
  const rotateXSpring = useSpring(0, springConfig);
  const rotateYSpring = useSpring(0, springConfig);
  
  useEffect(() => {
    rotateXSpring.set(isHovering ? mousePosition.rotateX : 0);
    rotateYSpring.set(isHovering ? mousePosition.rotateY : 0);
  }, [mousePosition, isHovering]);
  
  return (
    <motion.div 
      ref={cardRef}
      className="savings-card ultra-glass"
      style={{
        rotateX: rotateXSpring,
        rotateY: rotateYSpring,
        transformPerspective: 1000,
        transformStyle: "preserve-3d"
      }}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.3 }}
      onMouseMove={handleMouseMove}
      onHoverStart={() => setIsHovering(true)}
      onHoverEnd={() => setIsHovering(false)}
      whileHover={{
        boxShadow: "0 30px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.15) inset, 0 0 30px rgba(0, 209, 224, 0.15) inset"
      }}
    >
      <div className="card-shine"></div>
      <div className="card-noise"></div>
      <motion.div style={{ position: "relative", zIndex: 2 }}>
        <h2 className="savings-title">From Solo to Savings</h2>
        
        <div className="price-comparison">
          <motion.div 
            className="price-box"
            whileHover={{ 
              y: -10, 
              scale: 1.05,
              transition: { type: "spring", stiffness: 400, damping: 10 }
            }}
          >
            <motion.div 
              className="price-icon individual ultra-glass-light"
              whileHover={{ scale: 1.1 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <User size={24} color="white" />
              <div className="icon-glow"></div>
            </motion.div>
            <span className="price-amount">$80</span>
            <span className="price-label">By Yourself</span>
          </motion.div>
          
          <motion.div 
            className="price-arrow"
            animate={{ 
              x: [0, 10, 0],
              opacity: [0.7, 1, 0.7] 
            }}
            transition={{ 
              duration: 2, 
              repeat: Infinity,
              repeatType: "loop" 
            }}
          >
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 20H32M32 20L22 10M32 20L22 30" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </motion.div>
          
          <motion.div 
            className="price-box"
            whileHover={{ 
              y: -10, 
              scale: 1.05,
              transition: { type: "spring", stiffness: 400, damping: 10 }
            }}
          >
            <motion.div 
              className="price-icon group ultra-glass-light"
              whileHover={{ scale: 1.1 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <Users size={24} color="white" />
              <div className="icon-glow"></div>
            </motion.div>
            <motion.span 
              className="price-amount highlight"
              animate={{ 
                scale: [1, 1.03, 1],
                textShadow: [
                  "0 0 5px rgba(0, 209, 224, 0.3)",
                  "0 0 15px rgba(0, 209, 224, 0.6)",
                  "0 0 5px rgba(0, 209, 224, 0.3)"
                ]
              }}
              transition={{ 
                duration: 2, 
                repeat: Infinity,
                repeatType: "loop" 
              }}
            >
              $40
            </motion.span>
            <span className="price-label">Group Plan</span>
          </motion.div>
        </div>
        
        <motion.div 
          className="savings-result ultra-glass-medium"
          whileHover={{ 
            y: -5,
            boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.15) inset, 0 0 30px rgba(0, 209, 224, 0.2) inset"
          }}
        >
          <h3>Your Potential Savings</h3>
          <div className="savings-numbers">
            <motion.div 
              className="savings-item"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <span className="savings-label">Monthly</span>
              <motion.span 
                className="savings-amount"
                initial={{ scale: 0.5 }}
                animate={{ scale: 1 }}
                transition={{ delay: 1, duration: 0.4, type: "spring", stiffness: 300, damping: 10 }}
              >
                $40
              </motion.span>
            </motion.div>
            
            <div className="savings-divider"></div>
            
            <motion.div 
              className="savings-item"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              <span className="savings-label">Annually</span>
              <motion.span 
                className="savings-amount"
                initial={{ scale: 0.5 }}
                animate={{ scale: 1 }}
                transition={{ delay: 1.2, duration: 0.4, type: "spring", stiffness: 300, damping: 10 }}
              >
                $480
              </motion.span>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

// Enhanced CTA button with advanced hover effects
const EnhancedCTAButton = ({ children, ...props }) => {
  return (
    <motion.button 
      className="enhanced-cta glass-button"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay: 1 }}
      whileHover={{ 
        scale: 1.05, 
        boxShadow: "0 20px 40px rgba(255, 71, 117, 0.45), 0 0 0 2px rgba(255, 255, 255, 0.15) inset"
      }}
      whileTap={{ scale: 0.98 }}
      {...props}
    >
      <span className="button-text">{children}</span>
      <div className="button-icon-container">
        <ArrowRight size={18} className="button-icon" />
      </div>
      <motion.div 
        className="button-glow" 
        whileHover={{ opacity: 1 }}
        initial={{ opacity: 0 }}
      />
    </motion.button>
  );
};

// Enhanced customer satisfaction component
const EnhancedCustomerSatisfaction = () => (
  <motion.div 
    className="satisfaction-container"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: 1.2 }}
    whileHover={{ 
      scale: 1.05,
      boxShadow: "0 15px 30px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.15) inset"
    }}
  >
    <div className="stars-container">
      {[...Array(5)].map((_, i) => (
        <motion.div 
          key={i}
          initial={{ opacity: 0, scale: 0, rotate: -30 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ delay: 1.3 + i * 0.1, type: "spring" }}
          whileHover={{ scale: 1.2, rotate: 5 }}
        >
          <Star size={16} className="star filled" />
        </motion.div>
      ))}
    </div>
    <span className="satisfaction-text">90% Customer Satisfaction</span>
    <div className="satisfaction-glow"></div>
  </motion.div>
);

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);
  const heroRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    setIsVisible(true);
    
    // Check if mobile on mount and window resize
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check
    checkMobile();
    
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setScrollY(scrollPosition);
      
      // Only apply parallax effects on desktop
      if (!isMobile) {
        const heroElement = heroRef.current;
        if (heroElement) {
          // Enhanced parallax effect on scroll with varying speeds
          const blobs = heroElement.querySelectorAll('.organic-blob');
          blobs.forEach((blob, index) => {
            const speed = 0.03 + (index * 0.02);
            const rotate = scrollPosition * 0.02;
            blob.style.transform = `translateY(${scrollPosition * speed}px) rotate(${rotate}deg)`;
          });
          
          // Parallax for other elements - reduced effect to minimize content shift
          const title = heroElement.querySelector('.headline-slider');
          const features = heroElement.querySelector('.enhanced-features');
          const card = heroElement.querySelector('.savings-card');
          
          if (title) title.style.transform = `translateY(${scrollPosition * -0.05}px)`;
          if (features) features.style.transform = `translateY(${scrollPosition * -0.03}px)`;
          if (card) card.style.transform = `translateY(${scrollPosition * -0.01}px)`;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkMobile);
    };
  }, [isMobile]);

  // If mobile, render the mobile hero component
  if (isMobile) {
    return <HeroMobile />;
  }

  // Desktop hero component
  return (
    <section className="hero-section-wrapper teal-theme" ref={heroRef}>
      {/* Advanced background elements */}
      <div className="noise-overlay"></div>
      <OrganicBlobs />
      <EnhancedParticles />
      
      {/* Color overlay gradients */}
      <div className="gradient-overlay gradient-1"></div>
      <div className="gradient-overlay gradient-2"></div>
      
      <div className="hero-container">
        <div className="hero-content">
          {/* Fixed height headline container */}
          <HeadlineSlider />
          
          <motion.p 
            className="hero-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
            transition={{ duration: 0.5, delay: 0.8 }}
          >
            Join a circle or create your own to unlock <br className="hero-subtitle-break" />
            premium plans at group rates
          </motion.p>
          
          <motion.div className="enhanced-features">
            <EnhancedFeatureItem 
              mainText="No Credit Check" 
              supportingText="Join without affecting your credit score" 
              delay={0} 
            />
            <EnhancedFeatureItem 
              mainText="Save $480/Year" 
              supportingText="Real savings you can count on" 
              delay={1} 
            />
            <EnhancedFeatureItem 
              mainText="No Contracts " 
              supportingText="Switch services anytime" 
              delay={2} 
            />
          </motion.div>
          
          <EnhancedCTAButton>
            See How It Works
          </EnhancedCTAButton>
          
          <EnhancedCustomerSatisfaction />
        </div>
        
        <div className="hero-visual">
          <InteractiveSavingsCard />
        </div>
      </div>
      
      <motion.div 
        className="scroll-indicator"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.8, duration: 0.5 }}
      >
        <div className="mouse">
          <div className="mouse-wheel"></div>
        </div>
        <motion.span
          animate={{
            y: [0, 5, 0],
            opacity: [0.7, 1, 0.7]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            repeatType: "loop"
          }}
        >
          Scroll to explore
        </motion.span>
      </motion.div>
    </section>
  );
};

export default Hero;